// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import acceptBidHandler from "@steiner/App/Handlers/OpenBids/AcceptBid";
import { CustomAppState, OpenBidOrderModel, ReorderPadItemModel } from "@steiner/App/Store/Reducers";
import { getFormattedDate } from "@steiner/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

interface ShowColumns {
    showShipDate?: boolean;
    showReleaseNumber?: boolean;
    showBidStatus?: boolean;
}

interface OwnProps {
    openBid: OpenBidOrderModel;
    showColumns: ShowColumns;
    labels: { [key: string]: string };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const openBidsDetailsPageUrl = getPageLinkByPageType(state, "OpenBidsDetailsPage")?.url;
    const openBidsDetailsUrl = `${openBidsDetailsPageUrl}?OrderNumber=${ownProps.openBid.orderId}&GenerationId=${ownProps.openBid.generationId}`;
    return { openBidsDetailsUrl };
};

const mapDispatchToProps = {
    acceptBidHandler,
};

type Props = OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasToasterContext;

interface OpenBidsItemStyles {
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    orderDateLink?: LinkPresentationProps;
    bidNumberLink?: LinkPresentationProps;
    acceptBidButton?: ButtonPresentationProps;
}

export const openBidsItemStyles: OpenBidsItemStyles = {};

const shipViaIdRequestedBid = "REQUESTED BID";

const styles = openBidsItemStyles;
const OpenBidsItem: FC<Props> = ({ openBid, labels, showColumns, toaster, openBidsDetailsUrl, acceptBidHandler }) => {
    const isRequestedBid = openBid.shippingInformation?.shipVia?.shipViaId === shipViaIdRequestedBid;

    const acceptBid = (openBid: OpenBidOrderModel) => {
        acceptBidHandler({
            generationId: openBid.generationId,
            orderNumber: openBid.orderId,
            onSuccess: () => {
                toaster.addToast({
                    body: translate("The requested products have been added to your cart successfully."),
                    messageType: "success",
                });
            },
            onError: () => {
                toaster.addToast({
                    body: translate("An error occurred while trying to accept bid."),
                    messageType: "danger",
                });
            },
        });
    };

    const renderOrderDate = (openBid: OpenBidOrderModel) => {
        if (!openBid.orderDate) {
            return null;
        }

        const formattedDate = getFormattedDate(new Date(openBid.orderDate));
        if (isRequestedBid) {
            return formattedDate;
        }

        return (
            <Link href={openBidsDetailsUrl} {...styles.orderDateLink}>
                {formattedDate}
            </Link>
        );
    };

    const renderBidNumber = (openBid: OpenBidOrderModel) => {
        if (isRequestedBid) {
            return openBid.bidNumber;
        }

        return (
            <Link href={openBidsDetailsUrl} {...styles.bidNumberLink}>
                {openBid.bidNumber}
            </Link>
        );
    };

    return (
        <DataTableRow key={openBid.bidNumber} {...styles.dataTableRow}>
            <DataTableCell {...{ header: labels.date }} {...styles.dataTableCells}>
                {renderOrderDate(openBid)}
            </DataTableCell>
            <DataTableCell {...{ header: labels.bidNumber }} {...styles.dataTableCells}>
                {renderBidNumber(openBid)}
            </DataTableCell>
            <DataTableCell {...{ header: labels.poName }} {...styles.dataTableCells}>
                {openBid.customerPo}
            </DataTableCell>
            {showColumns.showShipDate && (
                <DataTableCell {...{ header: labels.shipDate }} {...styles.dataTableCells}>
                    {openBid.shipDate}
                </DataTableCell>
            )}
            {showColumns.showReleaseNumber && (
                <DataTableCell {...{ header: labels.releaseNumber }} {...styles.dataTableCells}>
                    {openBid.customerReleaseNumber}
                </DataTableCell>
            )}
            <DataTableCell {...{ header: labels.expirationDate }} {...styles.dataTableCells}>
                {openBid.bidExpireDate && getFormattedDate(new Date(openBid.bidExpireDate))}
            </DataTableCell>
            <DataTableCell {...{ header: labels.amount }} {...styles.dataTableCells}>
                {isRequestedBid ? "" : openBid.amount}
            </DataTableCell>
            {showColumns.showBidStatus && (
                <DataTableCell {...{ header: " " }} {...styles.dataTableCells}>
                    {isRequestedBid ? (
                        translate("In Progress")
                    ) : (
                        <Button onClick={() => acceptBid(openBid)} {...styles.acceptBidButton}>
                            {translate("Accept Bid")}
                        </Button>
                    )}
                </DataTableCell>
            )}
        </DataTableRow>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(OpenBidsItem));
